<template>
  <div class="column">
    <woot-modal-header :header-title="$t('AUTOMATION.ADD.TITLE')" />
    <div class="row modal-content">
      <div class="medium-12 columns">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: $v.automation.name.$error }"
          :error="
            $v.automation.name.$error
              ? $t('AUTOMATION.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="$v.automation.name.$touch"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: $v.automation.description.$error }"
          :error="
            $v.automation.description.$error
              ? $t('AUTOMATION.ADD.FORM.DESC.ERROR')
              : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          @blur="$v.automation.description.$touch"
        />
        <div class="event_wrapper">
          <label :class="{ error: $v.automation.event_name.$error }">
            {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            <select v-model="automation.event_name" @change="onEventChange()">
              <option
                v-for="event in automationRuleEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.value }}
              </option>
            </select>
            <span v-if="$v.automation.event_name.$error" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </label>
          <p v-if="hasAutomationMutated" class="info-message">
            {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
          </p>
        </div>
        <!-- // Conditions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
          </label>
          <div class="medium-12 columns filters-wrap">
            <filter-input-box
              v-for="(condition, i) in automation.conditions"
              :key="i"
              v-model="automation.conditions[i]"
              :grouped-filters="true"
              :filter-groups="formatAttributesToGroup(getAttributes(automation.event_name))"
              :filter-attributes="getAttributes(automation.event_name)"
              :input-type="getInputType(automation.conditions[i].attribute_key)"
              :operators="getOperators(automation.conditions[i].attribute_key)"
              :dropdown-values="getConditionDropdownValues(automation.conditions[i].attribute_key,automation.event_name)"
              :customDropDownValue="getCustomDropdownValue(automation.conditions[i])"
              :customEventDateDaysValue="getCustomEventDateDaysValue(automation.conditions[i])"
              :custom-attribute-type="getCustomAttributeType(automation.conditions[i].attribute_key)"
              :show-query-operator="i !== automation.conditions.length - 1"
              :show-user-input="showUserInput(automation.conditions[i].filter_operator)"
              :v="$v.automation.conditions.$each[i]"
              @resetFilter="resetFilter(i, automation.conditions[i])"
              @removeFilter="removeFilter(i)"
            />
            <div class="filter-actions">
              <woot-button
                icon="add"
                color-scheme="success"
                variant="smooth"
                size="small"
                @click="appendNewCondition"
              >
                {{ $t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL') }}
              </woot-button>
            </div>
          </div>
        </section>
        <!-- // Conditions End -->

        <!-- // Actions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </label>
          <div class="medium-12 columns filters-wrap">
            <automation-action-input
              v-for="(action, i) in automation.actions"
              :key="i"
              v-model="automation.actions[i]"
              :action-types="fetchActionTypes()"
              :custom-attributes="formatCustomAttributes(filterGroups)"
              :dropdown-values="
                getActionDropdownValues(automation.actions[i].action_name)
              "
              :v="$v.automation.actions.$each[i]"
              @removeAction="removeAction(i)"
            />
            <div class="filter-actions">
              <woot-button
                icon="add"
                color-scheme="success"
                variant="smooth"
                size="small"
                @click="appendNewAction"
              >
                {{ $t('AUTOMATION.ADD.ACTION_BUTTON_LABEL') }}
              </woot-button>
            </div>
          </div>
        </section>
        <!-- // Actions End -->
        <div class="medium-12 columns">
          <div class="modal-footer justify-content-end w-full">
            <woot-button class="button clear" @click.prevent="onClose">
              {{ $t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT') }}
            </woot-button>
            <woot-button @click="checkRecords">
              {{ $t('AUTOMATION.ADD.SUBMIT') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import filterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import automationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from '/app/javascript/shared/constants/countries.js';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
  CUSTOM_ATTRIBUTES_DATA,
} from './constants';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import actionQueryGenerator from 'dashboard/helper/actionQueryGenerator.js';
import conditionMixin from '../../../../../shared/mixins/conditionMixin';
import * as OPERATORS from '../../../../components/widgets/FilterInput/FilterOperatorTypes';
import { mapGetters } from 'vuex';
// import {} from "app/javascript/dashboard/helper/automationHelper.js";
import {CONDITION_DROPDOWN_OPTIONS} from "../../../../helper/automationHelper.js";
import automationMethodsMixin from 'dashboard/mixins/automations/methodsMixin';
import {
  getConditionOptions,
} from 'dashboard/helper/automationHelper';

export default {
  components: {
    filterInputBox,
    automationActionInput,
  },
  mixins: [alertMixin, conditionMixin,automationMethodsMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    records: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  validations: {
    automation: {
      name: {
        required,
      },
      description: {
        required,
      },
      event_name: {
        required,
      },
      conditions: {
        required,
        $each: {
          values: {
            required: requiredIf(prop => {
              return !(
                prop.filter_operator === 'is_present' ||
                prop.filter_operator === 'is_not_present'
              );
            }),
          },
          custom_event_name: {
            required: requiredIf(prop => {
              return !(prop.attribute_key !== 'custom_event');
            }),
          },
          days: {
            required: requiredIf(prop => {
              return !(prop.attribute_key !== 'custom_event');
            }),
          },
        },
      },
      actions: {
        required,
        $each: {
          action_params: {
            required,
          },
          notNeeded: {
            required: requiredIf(function(prop) {
              return this.fetchNameError(prop);
            }),
          },
        },
      },
    },
  },
  data() {
    return {
      automationTypes: JSON.parse(JSON.stringify(AUTOMATIONS)),
      automationRuleEvent: AUTOMATION_RULE_EVENTS[0].key,
      automationRuleEvents: this.checkAutomationRules(),
      automationActionTypes: AUTOMATION_ACTION_TYPES,
      automationMutated: false,
      attributeModel: 'contact_attribute',
      filtersFori18n: 'CONTACTS_FILTER',
      filterGroups: [],
      filterTypes: [],
      show: true,
      automation: {
        name: null,
        description: null,
        event_name: 'conversation_created',
        conditions: [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ],
        actions: [
          {
            action_name: 'assign_team',
            action_params: [],
          },
        ],
      },
      showDeleteConfirmationModal: false,
    };
  },
  computed: {
    ...mapGetters({
      userPlan: 'accounts/getUserPlan',
    }),
    conditions() {
      return this.setConditionAttributes(
        this.automationTypes[this.automation.event_name].conditions
      );
    },
    actions() {
      return this.automationTypes[this.automation.event_name].actions;
    },
    filterAttributes() {
      return this.filterTypes.map(type => {
        return {
          key: type.attributeKey,
          name: type.attributeName,
          attributeI18nKey: type.attributeI18nKey,
        };
      });
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      )
        return true;
      return false;
    },
  },
  mounted() {
    this.manifestCustomAttributes();
    this.setConditionAttributes(
      this.automationTypes[this.automation.event_name].conditions
    );
  },
  methods: {
    checkAutomationRules() {
      if (this.$store.getters['inboxes/checkWebInbox'].length > 0) {
        return AUTOMATION_RULE_EVENTS;
      }
      // eslint-disable-next-line array-callback-return
      AUTOMATION_RULE_EVENTS.map((value, index) => {
        if (value.key === 'event_created') {
          AUTOMATION_RULE_EVENTS.splice(index, 1);
        }
      });
      return AUTOMATION_RULE_EVENTS;
    },
    onEventChange() {
      if (
        this.automation.event_name === 'message_created' ||
        this.automation.event_name === 'message_updated'
      ) {
        this.automation.conditions = [
          {
            attribute_key: 'message_type',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else if (this.automation.event_name === 'contact_created') {
        this.automation.conditions = [
          {
            attribute_key: 'email',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }
       else if (this.automation.event_name === 'contact_updated') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }
       else if (this.automation.event_name === 'contact_attribute_changed') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }
       else if (this.automation.event_name === 'form_responder_completed') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }
      else if (this.automation.event_name === 'event_created') {
        this.automation.conditions = [
          {
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      } else {
        this.automation.conditions = [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          },
        ];
      }
      this.setConditionAttributes(
        this.automationTypes[this.automation.event_name].conditions
      );
    },
    fetchActionTypes() {
      let query_string = '';
      query_string += this.automation.conditions.map(value => {
        return (
          ' ' +
          value.attribute_key +
          ' ' +
          value.filter_operator +
          ' ' +
          value.values.id
        );
      });
      if (query_string.includes('message_type equal_to outgoing')) {
        return this.automationTypes.message_updated.actions;
      }
      if (
        this.automation.event_name === 'message_created' &&
        this.$store.getters['inboxes/checkWebInbox'].length === 0
      ) {
        this.automationTypes[this.automation.event_name].actions.map(
          // eslint-disable-next-line array-callback-return
          (value, index) => {
            if (value.key === 'trigger_event') {
              this.automationTypes[this.automation.event_name].actions.splice(index,1);
            }
          }
        );
      }
      return this.automationTypes[this.automation.event_name].actions;
    },
    fetchNameError(props) {
      const action_name =
        props !== undefined
          ? props.action_params
          : this.automation.actions[0].action_params;
      const data = this.automation.conditions.filter(item => {
        if (
          item.custom_event_name === '' ||
          item.custom_event_name === undefined
        ) {
          return false;
        }
        return item.custom_event_name === action_name;
      });
      return data.length > 0;
    },
    getAttributes(key) {
      return this.automationTypes[key].conditions;
    },
    getInputType(key) {
      let type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(condition => condition.key === key);
      if (type == null) {
        type = this.filterTypes.find(value => value.attributeKey === key);
      }
      return type.inputType;
    },
    getOperators(key) {
      let type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(condition => condition.key === key);
      if (type == null) {
        type = this.filterTypes.find(value => value.attributeKey === key);
      }
      return type.filterOperators;
    },
    getConditionDropdownValues(type, event_name) {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      switch (type) {
        case 'status':
          if (event_name.includes('message')) {
            return [
              { name: 'Sent', id: 'sent' },
              { name: 'delivered', id: 'delivered' },
              { name: 'read', id: 'read' },
              { name: 'failed', id: 'failed' },
            ];
          }
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
            {
              id: 'all',
              name: this.$t('CHAT_LIST.FILTER_ALL'),
            },
          ];
        case 'status_id':
          return this.$store.getters['accounts/getContactStatus'];
        case 'conversation_status':
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
            {
              id: 'all',
              name: this.$t('CHAT_LIST.FILTER_ALL'),
            },
          ];
        case 'contact_status':
          return this.$store.getters['accounts/getContactStatus'];
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'contact':
          return this.$store.getters['contacts/getContacts'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'campaign_id':
          return this.$store.getters['campaigns/getAllCampaigns'].map(i => {
            return {
              id: i.id,
              name: i.title,
            };
          });
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.id,
              name: i.title,
            };
          });
        case 'browser_language':
          return languages;
        case 'country_code':
          return countries;
        case 'message_type':
          return [
            {
              id: 'incoming',
              name: 'Incoming Message',
            },
            {
              id: 'outgoing',
              name: 'Outgoing Message',
            },
          ];
          // add
        case 'conversation_type':
          return CONDITION_DROPDOWN_OPTIONS.conversation_type;

        case 'message_recipient':
          return CONDITION_DROPDOWN_OPTIONS.message_recipient;

        default:
          return getConditionOptions({
            agents: this.$store.getters['agents/getAgents'],
            booleanFilterOptions: this.booleanFilterOptions,
            campaigns:this.$store.getters['campaigns/getAllCampaigns'],
            contacts:this.$store.getters['contacts/getContacts'],
            contactStatusList:[], // handling in cases already
            customAttributes: this.$store.getters['attributes/getAttributes'],
            inboxes:this.$store.getters['inboxes/getInboxes'],
            statusFilterOptions:[],
            teams: this.$store.getters['teams/getTeams'],
            languages,
            countries,
            type,
          });
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        default:
          return 'plain_text';
      }
    },
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_5;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    getActionDropdownValues(type) {
      switch (type) {
        case 'assign_team':
        case 'send_email_to_team':
          return this.$store.getters['teams/getTeams'];
        case 'add_label':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'send_message':
          return this.$store.getters['inboxes/getInboxes'];
        case 'change_contact_status':
          return this.$store.getters['accounts/getContactStatus'];
        default:
          return undefined;
      }
    },
    formatCustomAttributes(customAttribute) {
      let attributes = CUSTOM_ATTRIBUTES_DATA;
      customAttribute.map(value => {
        if (value.name === 'Custom Attributes') {
          attributes = [...attributes, ...value.attributes];
        }
        return attributes;
      });
      attributes = attributes.map(el=>{
        if (!el.hasOwnProperty('id')) {
          el.id = el.key;
        }
        return el;
      });

      return attributes;
    },
    appendNewCondition() {
      switch (this.automation.event_name) {
        case 'message_created':
        case 'message_updated':
          this.automation.conditions.push({
            attribute_key: 'message_type',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        case 'contact_created':
        case 'contact_updated':
        case 'contact_attribute_changed':
        case 'form_responder_completed':
          this.automation.conditions.push({
            attribute_key: 'email',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        case 'event_created':
          this.automation.conditions.push({
            attribute_key: 'status_id',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
        default:
          this.automation.conditions.push({
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
          });
          break;
      }
    },
    appendNewAction() {
      this.automation.actions.push({
        action_name: 'assign_team',
        action_params: [],
      });
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    async checkRecords() {
      if (
        this.userPlan.name !== null &&
        this.records.filter(record => record.active === true).length <
          this.userPlan.max_automations
      ) {
        this.submitAutomation();
      } else {
        // this.showAlert('please deactivate automation or upgrade plan');
        this.showAlert(this.$t('AUTOMATION.DISABLE_OR_UPDATE'));
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.automation.conditions[
        this.automation.conditions.length - 1
      ].query_operator = null;
      let old_conditions = this.automation.conditions;
      this.automation.conditions = filterQueryGenerator(
        this.automation.conditions
      ).payload;
      this.automation.conditions.forEach((condition, index) => {
        if (condition.attribute_key === 'content') {
          condition.values[0] = old_conditions[index].values;
        }
      });
      this.automation.actions = actionQueryGenerator(this.automation.actions);
      this.$emit('saveAutomation', this.automation);
    },
    resetFilter(index, currentCondition) {
      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;
      this.automation.conditions[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    getCustomDropdownValue(condition){
      if (condition.attribute_key !== 'custom_event') {
        return {};
      }

      const dropdownListDateRange = [
        {
          id: 'in_the_past',
          name: 'In the past',
        },
        {
          id: 'in_between',
          name: 'In between',
        },
      ];

      if (condition.days?.length === 1) {
        return dropdownListDateRange[0]
      }else if (condition.days?.length === 2) {
        return dropdownListDateRange[1]
      } else {
        return {};
      }

    },
    getCustomEventDateDaysValue(condition){
      if (condition.attribute_key !== 'custom_event') {
        return [];
      }

      if (condition.days?.length === 1) {
        return condition.days[0];
      }else if (condition.days?.length === 2) {
        return [...condition.days];
      }

    },


  },
};
</script>
<style lang="scss" scoped>
.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-border);
  background: var(--color-background-light);
  margin-bottom: var(--space-normal);
}

.filter-actions {
  margin-top: var(--space-normal);
}
.event_wrapper {
  select {
    margin: var(--space-zero);
  }
  .info-message {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    text-align: right;
  }
  margin-bottom: var(--space-medium);
}
</style>
